'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import {
  User,
  Calendar,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Clock,
  Video,
  Users,
  Check,
  Mail,
  Phone,
  Briefcase,
  MessageSquare,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { appointmentSchema, type AppointmentFormData } from '@/lib/validations';
import { TIME_SLOTS } from '@/lib/utils';
import { useSiteData } from '@/lib/dataCache';

type AppointmentType = {
  id: string;
  slug: string;
  title: string;
  description: string;
  icon: string;
  duration: number;
  color: string;
  active: boolean;
  order: number;
};

function toPascalCase(kebab: string): string {
  return kebab
    .split('-')
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : p))
    .join('');
}

function resolveLucideIcon(name: string | null | undefined): LucideIcon {
  if (!name) return Briefcase;
  const key = toPascalCase(name);
  const icons = LucideIcons as unknown as Record<string, LucideIcon>;
  return icons[key] || Briefcase;
}

const steps = [
  { number: 1, title: 'Personal Info', icon: User },
  { number: 2, title: 'Booking Details', icon: Calendar },
  { number: 3, title: 'Review & Submit', icon: CheckCircle2 },
];

const stepVariants = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

type PublicSettings = Partial<Record<string, string>>;

export default function AppointmentPage() {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const apptTypes = useSiteData<AppointmentType[]>(
    'appt-types',
    '/api/appointment-types',
    [],
  );
  // The in-person meeting's "location" line is admin-editable via
  // /admin/settings → Contact → Address (the `address` key). Fall back to
  // the legacy hard-coded value so the UI never shows an empty string
  // before the public settings arrive.
  const settings = useSiteData<PublicSettings>(
    'settings.public',
    '/api/settings/public',
    {}
  );
  const inPersonLocation =
    settings.in_person_location || settings.address || 'Dhaka, Bangladesh';

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    reset,
    formState: { errors },
  } = useForm<AppointmentFormData>({
    resolver: zodResolver(appointmentSchema),
    defaultValues: { meetingType: 'video' },
  });

  const values = watch();

  const nextStep = async () => {
    let fields: (keyof AppointmentFormData)[] = [];
    if (step === 1) fields = ['name', 'email', 'phone'];
    if (step === 2) fields = ['service', 'date', 'timeSlot', 'meetingType'];

    const valid = await trigger(fields);
    if (valid) setStep((s) => Math.min(s + 1, 3));
  };

  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  const onSubmit = async (data: AppointmentFormData) => {
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/appointment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to book appointment');
      toast.success('Appointment booked! Check your email for confirmation.');
      reset();
      setStep(1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  const progressPercent = ((step - 1) / (steps.length - 1)) * 100;

  return (
    <div className="appt-page">
      {/* Hero */}
      <section className="appt-hero">
        <div className="appt-hero-blob appt-hero-blob-1" />
        <div className="appt-hero-blob appt-hero-blob-2" />
        <div className="section-container">
          <motion.div
            className="appt-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">Book a Call</span>
            <h1 className="appt-hero-title">
              Schedule an <span className="gradient-text">Appointment</span>
            </h1>
            <p className="appt-hero-subtitle">
              Pick a time that works for you. A free 30-minute call to discuss your project,
              answer questions, or just say hello.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="appt-section">
        <div className="appt-container">
          {/* Progress */}
          <div className="appt-progress">
            <div className="appt-progress-track">
              <motion.div
                className="appt-progress-fill"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
              />
            </div>
            <div className="appt-progress-steps">
              {steps.map((s) => {
                const Icon = s.icon;
                const isActive = s.number === step;
                const isComplete = s.number < step;
                return (
                  <div
                    key={s.number}
                    className={`appt-progress-step ${isActive ? 'appt-progress-step-active' : ''} ${
                      isComplete ? 'appt-progress-step-complete' : ''
                    }`}
                  >
                    <div className="appt-progress-circle">
                      {isComplete ? <Check size={16} /> : <Icon size={16} />}
                    </div>
                    <span className="appt-progress-label">{s.title}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Form Card */}
          <motion.div
            className="appt-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <AnimatePresence mode="wait">
                {/* STEP 1 */}
                {step === 1 && (
                  <motion.div
                    key="step1"
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.3 }}
                    className="appt-step"
                  >
                    <div className="appt-step-header">
                      <div className="appt-step-icon">
                        <User size={22} />
                      </div>
                      <div>
                        <h2 className="appt-step-title">Personal Information</h2>
                        <p className="appt-step-subtitle">Tell me a bit about yourself.</p>
                      </div>
                    </div>

                    <div className="appt-field">
                      <label className="appt-label" htmlFor="appt-name">Full Name</label>
                      <input
                        id="appt-name"
                        type="text"
                        placeholder="Your full name"
                        className={`appt-input ${errors.name ? 'appt-input-error' : ''}`}
                        {...register('name')}
                      />
                      {errors.name && <span className="appt-error">{errors.name.message}</span>}
                    </div>

                    <div className="appt-field">
                      <label className="appt-label" htmlFor="appt-email">Email Address</label>
                      <input
                        id="appt-email"
                        type="email"
                        placeholder="you@example.com"
                        className={`appt-input ${errors.email ? 'appt-input-error' : ''}`}
                        {...register('email')}
                      />
                      {errors.email && <span className="appt-error">{errors.email.message}</span>}
                    </div>

                    <div className="appt-field">
                      <label className="appt-label" htmlFor="appt-phone">Phone Number</label>
                      <input
                        id="appt-phone"
                        type="tel"
                        placeholder="+880 1947527775"
                        className={`appt-input ${errors.phone ? 'appt-input-error' : ''}`}
                        {...register('phone')}
                      />
                      {errors.phone && <span className="appt-error">{errors.phone.message}</span>}
                    </div>
                  </motion.div>
                )}

                {/* STEP 2 */}
                {step === 2 && (
                  <motion.div
                    key="step2"
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.3 }}
                    className="appt-step"
                  >
                    <div className="appt-step-header">
                      <div className="appt-step-icon">
                        <Calendar size={22} />
                      </div>
                      <div>
                        <h2 className="appt-step-title">Booking Details</h2>
                        <p className="appt-step-subtitle">Choose a service, date and time.</p>
                      </div>
                    </div>

                    <div className="appt-field">
                      <label className="appt-label" htmlFor="appt-service">Service Type</label>
                      {apptTypes.length > 0 ? (
                        <div className="appt-meeting-grid" role="radiogroup" aria-label="Service type">
                          {apptTypes.map((t) => {
                            const Icon = resolveLucideIcon(t.icon);
                            const active = values.service === t.slug;
                            return (
                              <label
                                key={t.slug}
                                className={`appt-meeting-card ${active ? 'appt-meeting-card-active' : ''}`}
                                style={active ? { borderColor: t.color } : undefined}
                              >
                                <input
                                  type="radio"
                                  value={t.slug}
                                  className="appt-slot-input"
                                  {...register('service')}
                                />
                                <div
                                  className="appt-meeting-icon"
                                  style={{ color: t.color }}
                                >
                                  <Icon size={22} />
                                </div>
                                <div>
                                  <div className="appt-meeting-title">{t.title}</div>
                                  <div className="appt-meeting-sub">
                                    {t.duration} min{t.description ? ` · ${t.description.slice(0, 60)}${t.description.length > 60 ? '…' : ''}` : ''}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <select
                          id="appt-service"
                          className={`appt-input appt-select ${errors.service ? 'appt-input-error' : ''}`}
                          {...register('service')}
                          defaultValue=""
                        >
                          <option value="" disabled>Select a service</option>
                        </select>
                      )}
                      {errors.service && <span className="appt-error">{errors.service.message}</span>}
                    </div>

                    <div className="appt-field">
                      <label className="appt-label" htmlFor="appt-date">Preferred Date</label>
                      <input
                        id="appt-date"
                        type="date"
                        min={minDate}
                        className={`appt-input ${errors.date ? 'appt-input-error' : ''}`}
                        {...register('date')}
                      />
                      {errors.date && <span className="appt-error">{errors.date.message}</span>}
                    </div>

                    <div className="appt-field">
                      <label className="appt-label">Available Time Slots</label>
                      <div className="appt-slots">
                        {TIME_SLOTS.map((slot) => (
                          <label
                            key={slot}
                            className={`appt-slot ${values.timeSlot === slot ? 'appt-slot-active' : ''}`}
                          >
                            <input
                              type="radio"
                              value={slot}
                              className="appt-slot-input"
                              {...register('timeSlot')}
                            />
                            <Clock size={12} className="appt-slot-icon" />
                            <span>{slot}</span>
                          </label>
                        ))}
                      </div>
                      {errors.timeSlot && <span className="appt-error">{errors.timeSlot.message}</span>}
                    </div>

                    <div className="appt-field">
                      <label className="appt-label">Meeting Type</label>
                      <div className="appt-meeting-grid">
                        <label
                          className={`appt-meeting-card ${values.meetingType === 'video' ? 'appt-meeting-card-active' : ''}`}
                        >
                          <input
                            type="radio"
                            value="video"
                            className="appt-slot-input"
                            {...register('meetingType')}
                          />
                          <div className="appt-meeting-icon">
                            <Video size={22} />
                          </div>
                          <div>
                            <div className="appt-meeting-title">Video Call</div>
                            <div className="appt-meeting-sub">Google Meet or Zoom</div>
                          </div>
                        </label>
                        <label
                          className={`appt-meeting-card ${values.meetingType === 'in-person' ? 'appt-meeting-card-active' : ''}`}
                        >
                          <input
                            type="radio"
                            value="in-person"
                            className="appt-slot-input"
                            {...register('meetingType')}
                          />
                          <div className="appt-meeting-icon">
                            <Users size={22} />
                          </div>
                          <div>
                            <div className="appt-meeting-title">In-Person</div>
                            <div className="appt-meeting-sub">{inPersonLocation}</div>
                          </div>
                        </label>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* STEP 3 */}
                {step === 3 && (
                  <motion.div
                    key="step3"
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.3 }}
                    className="appt-step"
                  >
                    <div className="appt-step-header">
                      <div className="appt-step-icon">
                        <CheckCircle2 size={22} />
                      </div>
                      <div>
                        <h2 className="appt-step-title">Review & Submit</h2>
                        <p className="appt-step-subtitle">Confirm the details below and book.</p>
                      </div>
                    </div>

                    <div className="appt-field">
                      <label className="appt-label" htmlFor="appt-message">
                        Additional Message (Optional)
                      </label>
                      <textarea
                        id="appt-message"
                        rows={4}
                        placeholder="Anything else you'd like me to know..."
                        className="appt-input appt-textarea"
                        {...register('message')}
                      />
                    </div>

                    <div className="appt-summary">
                      <h3 className="appt-summary-title">
                        <Calendar size={16} />
                        Booking Summary
                      </h3>
                      <div className="appt-summary-list">
                        <div className="appt-summary-row">
                          <span className="appt-summary-key">
                            <User size={14} /> Name
                          </span>
                          <span className="appt-summary-val">{values.name || '—'}</span>
                        </div>
                        <div className="appt-summary-row">
                          <span className="appt-summary-key">
                            <Mail size={14} /> Email
                          </span>
                          <span className="appt-summary-val">{values.email || '—'}</span>
                        </div>
                        <div className="appt-summary-row">
                          <span className="appt-summary-key">
                            <Phone size={14} /> Phone
                          </span>
                          <span className="appt-summary-val">{values.phone || '—'}</span>
                        </div>
                        <div className="appt-summary-row">
                          <span className="appt-summary-key">
                            <Briefcase size={14} /> Service
                          </span>
                          <span className="appt-summary-val">
                            {apptTypes.find((t) => t.slug === values.service)?.title || values.service || '—'}
                          </span>
                        </div>
                        <div className="appt-summary-row">
                          <span className="appt-summary-key">
                            <Calendar size={14} /> Date
                          </span>
                          <span className="appt-summary-val">
                            {values.date
                              ? new Date(values.date + 'T00:00:00').toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  month: 'long',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : '—'}
                          </span>
                        </div>
                        <div className="appt-summary-row">
                          <span className="appt-summary-key">
                            <Clock size={14} /> Time
                          </span>
                          <span className="appt-summary-val">{values.timeSlot || '—'}</span>
                        </div>
                        <div className="appt-summary-row">
                          <span className="appt-summary-key">
                            {values.meetingType === 'in-person' ? <Users size={14} /> : <Video size={14} />}
                            Meeting
                          </span>
                          <span className="appt-summary-val">
                            {values.meetingType === 'in-person' ? 'In-Person' : 'Video Call'}
                          </span>
                        </div>
                        {values.message && (
                          <div className="appt-summary-row appt-summary-row-msg">
                            <span className="appt-summary-key">
                              <MessageSquare size={14} /> Message
                            </span>
                            <span className="appt-summary-val">{values.message}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Nav Buttons */}
              <div className="appt-nav">
                {step > 1 ? (
                  <button type="button" onClick={prevStep} className="appt-btn appt-btn-ghost">
                    <ArrowLeft size={16} />
                    Previous
                  </button>
                ) : (
                  <span />
                )}

                {step < 3 ? (
                  <button type="button" onClick={nextStep} className="appt-btn appt-btn-primary">
                    Next Step
                    <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="appt-btn appt-btn-primary"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={16} className="appt-spin" />
                        Booking...
                      </>
                    ) : (
                      <>
                        <Check size={16} />
                        Confirm Booking
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
